import { FC, HTMLAttributes, useMemo } from 'react'
import moment from 'moment'
import {
  AutocompleteOption,
  ConfiguredColumn,
  CurrentSortParams,
  EntityType,
  ObjectDataRecord,
  RestrictionDTO,
  SortDirection,
} from 'src/types'
import { Box, Grid } from '@mui/material'
import { GridArrowDownwardIcon, GridArrowUpwardIcon } from '@mui/x-data-grid'

import { MappedCheckbox } from '@components/MappedCheckbox'

import { SORT_DIRECTION } from '@hooks'
import {
  getColumnObjectExpandedDisplayValue,
  getTimeFromSeconds,
  isNotEmpty,
  isObjectValueType,
  renderBooleanValueCell,
  renderJSValueCell,
  renderJSXValueCell,
} from '@helpers'
import { BIND_TYPE, FIELD_VALUE_FORMAT, FIELD_VALUE_TYPE } from '@constants'
import { RESTRICTION_TYPE } from '@types/restriction'

import { getDropDownListColumnWidth, getDropDownListTotalWidth } from './getDropDownListColumnWidth'

export type DropDownListLineProps = {
  dropDownList: EntityType | undefined
  combinedData: ObjectDataRecord[]
  option: AutocompleteOption
  liProps: HTMLAttributes<HTMLLIElement>
  columns: ObjectDataRecord[]
  currentSort: CurrentSortParams | null
  isDialogWindow: boolean
  pkFieldName?: string
  isLoading?: boolean
  showSortOrder: boolean
  onUpdateRestrictions: (restrictions: RestrictionDTO) => void
  columnsToSort?: ObjectDataRecord
}

export type DropDownListHeadersProps = Pick<
  DropDownListLineProps,
  'columns' | 'onUpdateRestrictions' | 'isLoading' | 'showSortOrder' | 'columnsToSort'
>

const getColHeader = (
  title: string,
  code: string,
  currentSort: CurrentSortParams,
  sortKey: string,
  bindType: string,
  handleUpdateRestrictions: (restrictions: RestrictionDTO | null) => void,
  columnWidth: number,
  isLoading?: boolean,
  showSortOrder?: boolean,
  priority?: number
) => {
  const showDirection = currentSort?.sortDirection
  const isSortable = bindType !== BIND_TYPE.JSX && bindType !== BIND_TYPE.JS

  const handleHeaderClick = () => {
    if (!isSortable) return
    let nextDirection: SortDirection | null = SORT_DIRECTION.ASC

    if (currentSort?.sortDirection === SORT_DIRECTION.ASC) nextDirection = SORT_DIRECTION.DESC
    if (currentSort?.sortDirection === SORT_DIRECTION.DESC) nextDirection = null
    handleUpdateRestrictions({
      fieldName: title,
      fieldCode: code,
      restrictionType: RESTRICTION_TYPE.SORT,
      value: {
        columnToSort: sortKey,
        sortDirection: nextDirection || undefined,
        priority: currentSort.priority,
      },
    })
  }

  return (
    <Grid
      container
      item
      alignItems='center'
      justifyContent='flex-start'
      title={title}
      sx={{
        borderRight: `1px solid #303030`,
        boxSizing: 'border-box',
        flex: `0 0 ${columnWidth}px`,
        minWidth: columnWidth,
        maxWidth: columnWidth,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        minHeight: 25,
        backgroundColor: theme => theme.palette.background.headerTable,
        maxHeight: 52,
        px: '4px',
        pointerEvents: isLoading ? 'none' : 'auto',
        '&:last-child': {
          borderRight: `none`,
        },
      }}
      onClick={handleHeaderClick}
    >
      {title}
      {showDirection && isSortable && priority !== undefined && (
        <Box sx={{ display: 'flex' }}>
          {showDirection === SORT_DIRECTION.ASC ? (
            <GridArrowUpwardIcon />
          ) : (
            <GridArrowDownwardIcon />
          )}
          {showSortOrder && priority + 1}
        </Box>
      )}
    </Grid>
  )
}

export const DropDownListHeaders: FC<DropDownListHeadersProps> = ({
  columns,
  isLoading,
  onUpdateRestrictions,
  showSortOrder,
  columnsToSort,
}) => {
  const totalWidth = getDropDownListTotalWidth(columns)

  return (
    <Grid
      sx={{
        display: 'block',
        margin: 0,
        padding: 0,
      }}
    >
      <Grid
        container
        flexWrap='nowrap'
        sx={{
          m: 0,
          p: 0,
          minWidth: totalWidth,
          width: totalWidth,
          boxSizing: 'border-box',
          border: `1px solid #303030`,
          borderLeft: 'none',
          borderTop: 'none',
        }}
      >
        {columns.map(column => {
          const priority = columnsToSort?.[column.code]?.priority
          const columnWidth = getDropDownListColumnWidth(column)

          return getColHeader(
            column.title,
            column.code,
            column.sort,
            column.sortKey,
            column.bindType,
            onUpdateRestrictions,
            columnWidth,
            isLoading,
            showSortOrder,
            priority
          )
        })}
      </Grid>
    </Grid>
  )
}

export const DropDownListLine: FC<DropDownListLineProps> = ({
  onUpdateRestrictions: handleUpdateRestrictions,
  option,
  currentSort,
  isLoading,
  combinedData,
  liProps,
  columns,
  isDialogWindow,
  pkFieldName = '_id',
  showSortOrder,
}) => {
  const currentDataRowIndex = combinedData.findIndex(data => data[pkFieldName] === option.id)

  const renderValue = (column: ConfiguredColumn) => {
    const asCheckbox =
      column.valueFormat === FIELD_VALUE_FORMAT.NUMBER ||
      column.valueFormat === FIELD_VALUE_FORMAT.BOOLEAN

    if (
      (column.valueType === FIELD_VALUE_TYPE.DATETIME ||
        (column.pathArray?.length &&
          column.pathArray[column.pathArray.length - 1]?.valueType ===
            FIELD_VALUE_TYPE.DATETIME)) &&
      column.formatDate
    ) {
      if (!moment(combinedData[currentDataRowIndex][column.code]).isValid()) {
        return combinedData[currentDataRowIndex][column.code]
      }

      return moment(combinedData[currentDataRowIndex][column.code]).format(column.formatDate)
    }

    if (asCheckbox && column.valueFormat) {
      return (
        <Box sx={{ mt: 0.5 }}>
          <MappedCheckbox
            viewOnly
            valueFormat={column.valueFormat}
            outerValue={
              combinedData[currentDataRowIndex][column.code] as unknown as number | boolean
            }
          />
        </Box>
      )
    }

    if (column.valueFormat === FIELD_VALUE_FORMAT.TIME) {
      return getTimeFromSeconds(combinedData[currentDataRowIndex][column.code])
    }

    const srcObj = combinedData[currentDataRowIndex] || {}

    if (column.bindType === BIND_TYPE.JSX) {
      return renderJSXValueCell(column.value, srcObj)
    }

    if (column.bindType === BIND_TYPE.JS) {
      return renderJSValueCell(column.value, srcObj)
    }

    if (isObjectValueType(column.valueType)) {
      if (column.objectValue) {
        const displayValue = getColumnObjectExpandedDisplayValue(srcObj, column)

        return isNotEmpty(displayValue) ? String(displayValue) : ''
      }

      return ''
    }

    if (column.valueType === FIELD_VALUE_TYPE.BOOLEAN) {
      return renderBooleanValueCell(combinedData[currentDataRowIndex][column.code])
    }

    return combinedData[currentDataRowIndex][column.code]
  }

  const renderTitle = (column: ConfiguredColumn) => {
    const srcObj = combinedData[currentDataRowIndex] || {}

    if (isObjectValueType(column.valueType)) {
      if (column.objectValue) {
        const displayValue = getColumnObjectExpandedDisplayValue(srcObj, column)

        return isNotEmpty(displayValue) ? String(displayValue) : ''
      }

      return ''
    }

    const value = combinedData[currentDataRowIndex][column.code]

    return isNotEmpty(value) ? String(value) : ''
  }

  const columnsToSort = useMemo(
    () =>
      columns
        ?.filter(column => column?.sort?.sortDirection)
        .sort((a, b) => Number(a.sort?.priority) - Number(b.sort?.priority))
        .reduce((prev, currentValue, index) => {
          return {
            ...prev,
            [currentValue.code]: {
              ...currentValue.sort,
              priority: index,
            },
          }
        }, {}) || {},
    [columns]
  )

  const totalWidth = getDropDownListTotalWidth(columns)

  return (
    <>
      {liProps['data-option-index'] === 0 ? (
        <DropDownListHeaders
          columns={columns}
          columnsToSort={columnsToSort}
          isLoading={isLoading}
          showSortOrder={showSortOrder}
          onUpdateRestrictions={handleUpdateRestrictions}
        />
      ) : null}
      <li
        {...liProps}
        style={{
          display: 'block',
          margin: 0,
          padding: 0,
          height: 25,
          minHeight: 25,
          maxHeight: 25,
          boxSizing: 'border-box',
        }}
      >
        <Grid
          container
          flexWrap='nowrap'
          sx={{
            m: 0,
            p: 0,
            height: 25,
            minHeight: 25,
            maxHeight: 25,
            minWidth: totalWidth,
            width: totalWidth,
            boxSizing: 'border-box',
          }}
        >
          {columns.map(column => {
            const columnWidth = getDropDownListColumnWidth(column)

            return currentDataRowIndex !== -1 ? (
              <Grid
                key={column.code}
                item
                title={renderTitle(column)}
                sx={{
                  pl: 1,
                  borderRight: `1px solid #303030`,
                  boxSizing: 'border-box',
                  flex: `0 0 ${columnWidth}px`,
                  minWidth: columnWidth,
                  maxWidth: columnWidth,
                  height: 25,
                  minHeight: 25,
                  maxHeight: 25,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  '&:last-child': {
                    borderRight: 'none',
                  },
                }}
              >
                {renderValue(column)}
              </Grid>
            ) : (
              <></>
            )
          })}
        </Grid>
      </li>
    </>
  )
}
